/**
 * Rapid Save + Same-Day Rule — /whats-new/rapid-save-same-day-rule
 *
 * Standalone launch 1-pager that pairs the two workflow shifts:
 *   (1) Rapid Save · 4-field doctor-side one-tap walk-in log with a
 *       facility-locked "Came via" channel (a Polyclinic can never be
 *       misfiled as a Hospital by construction).
 *   (2) Same-Day Rule · DHF and Digital Prescriptions can only be
 *       issued on the same calendar day as the visit — no back-dating.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, Share2, Copy, Check, Zap, Lock, MapPin,
  Clock, ArrowRight, AlertTriangle,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/rapid-save-same-day-rule';
const SHARE_MESSAGE = (
  `DocSite just shipped two workflow shifts that clean up the "patient scans QR, goes home, asks 3 days later" abuse loop.\n\n` +
  `1. RAPID SAVE — log a walk-in in 4 fields (Name · Mobile · Facility · Visit date). Facility auto-locks the channel, so a Polyclinic can never be misfiled as a Hospital.\n\n` +
  `2. SAME-DAY RULE — Digital Health Files and Digital Prescriptions can only be issued on the same calendar day as the visit. Late check-ins save the visit history only.\n\n` +
  `Read more: ${SHARE_URL}`
);

const PILLARS = [
  { icon: Zap, color: 'sky', tag: 'Composer', title: 'One-tap Rapid Save',
    body: 'A dashed "Rapid Save zone" now sits at the top of Log-a-visit. Fill 4 fields — Name, Mobile, Facility, Visit date — and hit Rapid Save. The full clinical composer is still available; Rapid is for the doctor who wants to log the row now and finish the DHF between patients.' },
  { icon: Lock, color: 'emerald', tag: 'Data integrity', title: 'Facility-locked "Came via"',
    body: 'The "Came via" channel is now derived from the facility\'s declared type (Polyclinic / Hospital / Clinic / Surgery Center / Institution). If you pick TDH — a Polyclinic — the system will not let it be filed as a Hospital walk-in. Ever. Locked by construction, not by validation.' },
  { icon: Clock, color: 'amber', tag: 'DHF Gate', title: 'Same-day only',
    body: 'DHF creation now returns 403 if the visit was more than 0 calendar days ago (India Standard Time). Digital Prescriptions cascade from the DHF, so they inherit the same gate. Visit history is still preserved — only the sealed clinical artefact is blocked from back-dating.' },
  { icon: AlertTriangle, color: 'rose', tag: 'Patient side', title: 'Loud bullet advisory on check-in',
    body: 'The public /dr/{slug}/checkin page now shows an amber bullet list at the top: DHF free-only-same-day · DPP signed & verifiable · monitor progress · late check-ins save visit history only, no DHF, no prescription. Patients understand the deal before they leave the clinic.' },
];

const COLOR = {
  sky:     'bg-sky-100 text-sky-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  amber:   'bg-amber-100 text-amber-700',
  rose:    'bg-rose-100 text-rose-700',
};

const NUMBERS = [
  { n: '4',   label: 'Fields to Rapid Save: Patient name · Mobile (10-digit) · Facility · Visit date' },
  { n: '5',   label: 'Facility types the "Came via" channel auto-derives from: Clinic · Polyclinic · Hospital · Surgery Center · Institution' },
  { n: '0',   label: 'Back-dated DHFs allowed. 403 returned when visit_date is not today (IST)' },
  { n: '4',   label: 'Amber bullet advisory items on the clinic check-in page — same-day rule made unmissable' },
  { n: '2',   label: 'Cascade endpoints the same-day gate protects — DHF creation and Digital Prescription issuance' },
  { n: '10',  label: 'Digits enforced on patient mobile at Rapid Save time — no free-text room for errors' },
];

export default function RapidSaveSameDayLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(SHARE_MESSAGE); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: 'DocSite · Rapid Save + Same-Day Rule', text: SHARE_MESSAGE, url: SHARE_URL }); } catch { /* cancelled */ } }
    else doCopy();
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: 'Rapid Save + Same-Day Rule — one-tap walk-in logging plus a hard check-in-today gate on DHF and Digital Prescriptions',
    datePublished: '2026-08-10',
    author: { '@type': 'Organization', name: 'DocSite' },
    publisher: { '@type': 'Organization', name: 'DocSite', logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo.png' } },
    mainEntityOfPage: SHARE_URL,
    description: 'Rapid Save · 4-field walk-in log · facility-locked channel. Same-day rule · DHF and Digital Prescriptions can only be issued on the day of the visit.',
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>Rapid Save + Same-Day Rule — walk-in log &amp; back-date lock · DocSite</title>
        <meta name="description" content="One-tap walk-in logging plus a hard same-day rule on DHF and Digital Prescriptions. Stops the scan-QR-and-forget abuse loop." />
        <link rel="canonical" href={SHARE_URL} />
        <meta property="og:title" content="Rapid Save + Same-Day Rule · DocSite" />
        <meta property="og:description" content="4-field walk-in log with facility-locked channel + hard back-date lock on DHF and Digital Prescriptions." />
        <meta property="og:url" content={SHARE_URL} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10 md:py-14">
        <nav className="text-xs text-slate-500 mb-4" aria-label="Breadcrumb">
          <Link to="/whats-new" className="hover:text-slate-700 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to What's New</Link>
        </nav>

        <header className="max-w-4xl mb-10">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-sky-700 flex items-center gap-1.5">
            <Sparkles size={12} /> 10 August 2026 · Rapid Save + Same-Day Rule
          </p>
          <h1 className="font-medium text-slate-900 mt-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 3.5vw, 3.75rem)', lineHeight: 1.1 }}>
            Log the walk-in in 4 fields. Then close the back-dating loop.
          </h1>
          <p className="text-base text-slate-600 mt-4 max-w-2xl leading-relaxed">
            Two workflow shifts, one story. Patients used to scan the clinic QR, walk out without self-onboarding, then WhatsApp the doctor three days later asking for a prescription and a health file. That loop is now closed on both ends — the doctor can save the visit in seconds, and the system will refuse to seal a clinical file dated any earlier than today.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={share} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700"><Share2 size={14} /> Share this launch</button>
            <button onClick={doCopy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300">
              {copied ? <><Check size={14} className="text-emerald-600" /> Copied</> : <><Copy size={14} /> Copy the story</>}
            </button>
          </div>
        </header>

        <section className="max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <article key={i} className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${COLOR[p.color]}`}><Icon size={16} /></span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold ${p.color === 'sky' ? 'text-sky-700' : p.color === 'emerald' ? 'text-emerald-700' : p.color === 'amber' ? 'text-amber-700' : 'text-rose-700'}`}>{p.tag}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.body}</p>
              </article>
            );
          })}
        </section>

        <section className="max-w-4xl mb-12">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-slate-500 mb-3">By the numbers</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NUMBERS.map((k, i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-4">
                <div className="text-3xl font-black text-sky-700 leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>{k.n}</div>
                <p className="text-[11px] text-slate-600 mt-2 leading-relaxed">{k.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mb-12 rounded-2xl bg-white border border-slate-200 p-6">
          <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-emerald-700 mb-2">Why locking the facility type matters</p>
          <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>The relationship is stored, not typed.</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Every location in DocSite carries a <em>type</em> field — Clinic, Polyclinic, Hospital, Surgery Center, or Institution — set once when the doctor adds it. The "Came via" channel on every new visit is derived from that type, not typed by hand. So if TDH is a Polyclinic in your Locations list, it will always be a Polyclinic on the visit row, on the Rx PDF, on the DHF header, and in every export — no matter how many times the row is edited. This isn't validation; it's an architectural lock.
          </p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed inline-flex items-center gap-2"><MapPin size={14} className="text-emerald-700" /> If you haven't set types on your locations yet, do it once and every future row is stamped correctly forever.</p>
        </section>

        <div className="max-w-4xl rounded-2xl border border-slate-200 bg-white p-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Try Rapid Save on your next walk-in.</p>
            <p className="text-xs text-slate-500 mt-0.5">Open any patient row → the dashed "Rapid Save zone" sits at the top of the composer.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/register?as=doctor&from=rapid_save_same_day" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">Start free in 3 clicks <ArrowRight size={14} /></Link>
            <Link to="/whats-new" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300">← Back to launch timeline</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
